import { useEffect, useMemo, useRef, useState } from "react";
import { Filter, Check, X } from "lucide-react";

// Excel-style column header filter: funnel icon → searchable checklist of distinct values.
// `options`: [{ value, label, count? }]  `selected`: array of values  `onChange(nextArray)`
export function ColumnFilter({ label, options, selected, onChange, testid, align = "left" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const active = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const shown = useMemo(
    () => options.filter((o) => !q || String(o.label).toLowerCase().includes(q.toLowerCase())),
    [options, q]
  );

  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div className="relative inline-flex items-center gap-1" ref={ref}>
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`p-0.5 rounded hover:bg-[#E5E2DC] ${active ? "text-[#1E3F20]" : "text-[#8A8577]"}`}
        title={`Filter ${label}`}
        data-testid={testid}
        aria-pressed={active}
      >
        <Filter className="w-3.5 h-3.5" fill={active ? "currentColor" : "none"} />
      </button>
      {active && (
        <span className="text-[9px] font-black bg-[#1E3F20] text-white rounded-full px-1.5 leading-4" data-testid={`${testid}-count`}>
          {selected.length}
        </span>
      )}
      {open && (
        <div
          className={`absolute top-full mt-1 z-30 w-60 bg-white border border-[#E5E2DC] rounded-lg shadow-xl p-2 normal-case tracking-normal text-sm font-normal text-[#1A2E1C] ${align === "right" ? "right-0" : "left-0"}`}
          data-testid={`${testid}-menu`}
        >
          {options.length > 6 && (
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="w-full border border-[#E5E2DC] rounded px-2 py-1 text-sm mb-2 focus:outline-none focus:ring-1 focus:ring-[#1E3F20]"
              data-testid={`${testid}-search`}
            />
          )}
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {shown.length === 0 && <div className="text-xs text-[#8A8577] px-2 py-1">No matches</div>}
            {shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={String(o.value)}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-[#F2EDE4] ${on ? "bg-[#F2EDE4]" : ""}`}
                  data-testid={`${testid}-opt-${String(o.value).replace(/[^a-zA-Z0-9_-]/g, "_")}`}
                >
                  <span className={`w-4 h-4 rounded border grid place-items-center ${on ? "bg-[#1E3F20] border-[#1E3F20] text-white" : "border-[#C9C4B8]"}`}>
                    {on && <Check className="w-3 h-3" />}
                  </span>
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.count != null && <span className="text-[11px] text-[#8A8577]">{o.count}</span>}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#E5E2DC]">
            <button type="button" onClick={() => { onChange([]); setQ(""); }} className="text-xs text-[#5C6B5E] hover:text-[#1A2E1C] inline-flex items-center gap-1" data-testid={`${testid}-clear`}>
              <X className="w-3 h-3" /> Clear
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-semibold text-[#1E3F20]" data-testid={`${testid}-done`}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Build distinct {value,label,count} options from rows.
export function distinctOptions(rows, valueKey, labelKey = valueKey, emptyLabel = "—") {
  const m = new Map();
  rows.forEach((r) => {
    const v = r[valueKey] ?? "";
    const cur = m.get(v) || { value: v, label: r[labelKey] || emptyLabel, count: 0 };
    cur.count += 1;
    m.set(v, cur);
  });
  return [...m.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

// Date range header filter (from/to, inclusive, on YYYY-MM-DD strings).
export function DateRangeFilter({ label, range, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = !!(range.from || range.to);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="relative inline-flex items-center gap-1" ref={ref}>
      <span>{label}</span>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`p-0.5 rounded hover:bg-[#E5E2DC] ${active ? "text-[#1E3F20]" : "text-[#8A8577]"}`} data-testid={testid} aria-pressed={active}>
        <Filter className="w-3.5 h-3.5" fill={active ? "currentColor" : "none"} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-30 w-60 bg-white border border-[#E5E2DC] rounded-lg shadow-xl p-3 normal-case tracking-normal text-sm font-normal text-[#1A2E1C] space-y-2" data-testid={`${testid}-menu`}>
          <label className="block text-xs text-[#5C6B5E]">From
            <input type="date" value={range.from || ""} max={range.to || undefined} onChange={(e) => onChange({ ...range, from: e.target.value })} className="mt-0.5 w-full border border-[#E5E2DC] rounded px-2 py-1" data-testid={`${testid}-from`} />
          </label>
          <label className="block text-xs text-[#5C6B5E]">To
            <input type="date" value={range.to || ""} min={range.from || undefined} onChange={(e) => onChange({ ...range, to: e.target.value })} className="mt-0.5 w-full border border-[#E5E2DC] rounded px-2 py-1" data-testid={`${testid}-to`} />
          </label>
          <div className="flex justify-between items-center pt-2 border-t border-[#E5E2DC]">
            <button type="button" onClick={() => onChange({ from: "", to: "" })} className="text-xs text-[#5C6B5E] hover:text-[#1A2E1C] inline-flex items-center gap-1" data-testid={`${testid}-clear`}><X className="w-3 h-3" /> Clear</button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-semibold text-[#1E3F20]">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
